import React, { useState, useEffect } from 'react';
import { parseAIResponse, processMarkdown } from '../../utils/aiResponseParser';
import VisualizationRenderer, { VisualizationData } from './VisualizationRenderer';
import ChartErrorBoundary from './ChartErrorBoundary';
import { OrganisationSettings } from '../../services/organisationSettingsService';
import './AIReport.css';

interface AIReportProps {
  responseText: string;
  fileName?: string;
  isKPIAnalysisHighAccuracy?: boolean;
  kpiCodes?: string[];
  organizationSettings?: OrganisationSettings | null;
  userQuestion?: string;
}

interface ParsedSection {
  type: 'text' | 'visualization';
  content: string | VisualizationData;
}

// Define types for ReactMarkdown component children
interface CodeProps {
  inline?: boolean;
  className?: string;
  children: React.ReactNode;
  [key: string]: any;
}

/**
 * Strip Word formatting markers from response text for clean browser display
 * Removes marker tags but KEEPS content (even EXCLUDE_FROM_WORD sections)
 * Those sections will be filtered out during Word export only
 */
function stripWordFormatMarkers(text: string): string {
  if (!text) return text;
  
  let result = text;
  
  // Remove marker sections that should not appear in browser at all
  const removeContentMarkers = [
    { start: '<!-- WORD_HEADER_START -->', end: '<!-- WORD_HEADER_END -->' },
    { start: '<!-- WORD_FOOTER_START -->', end: '<!-- WORD_FOOTER_END -->' },
    { start: '<!-- WORD_TITLE_PAGE_START -->', end: '<!-- WORD_TITLE_PAGE_END -->' },
  ];
  
  removeContentMarkers.forEach(marker => {
    let startIdx = result.indexOf(marker.start);
    while (startIdx !== -1) {
      const endIdx = result.indexOf(marker.end, startIdx);
      if (endIdx === -1) break;
      
      // Remove entire section including content
      result = result.substring(0, startIdx) + 
               result.substring(endIdx + marker.end.length);
      
      startIdx = result.indexOf(marker.start);
    }
  });
  
  // Remove only the marker tags (but keep content) for EXCLUDE_FROM_WORD sections
  // This allows content to display in browser but be filtered during Word export
  result = result.replace(/<!-- EXCLUDE_FROM_WORD_START -->/g, '');
  result = result.replace(/<!-- EXCLUDE_FROM_WORD_END -->/g, '');
  
  // Remove standalone page break markers
  result = result.replace(/<!-- PAGE_BREAK -->/g, '');
  
  // Clean up excessive whitespace left by removed markers
  result = result.replace(/\n{3,}/g, '\n\n').trim();
  
  console.log('📄 AIReport: Stripped Word formatting markers (kept EXCLUDE content for browser display)');
  
  return result;
}

const AIReport: React.FC<AIReportProps> = ({ responseText, fileName = 'Financial Report', isKPIAnalysisHighAccuracy = false, kpiCodes = [], organizationSettings, userQuestion }) => {
  const [showVerification, setShowVerification] = useState(false);
  
  // Debug organization settings
  console.log('AIReport - Organization Settings:', organizationSettings);
  console.log('AIReport - Logo URL:', organizationSettings?.logoUrl);
  console.log('AIReport - User Question:', userQuestion);
  
  // Build full logo URL
  const getFullLogoUrl = (logoUrl: string | undefined) => {
    if (!logoUrl) return "/LuminaAilogoPNG.png";
    
    // If it's already a full URL, return as is
    if (logoUrl.startsWith('http')) return logoUrl;
    
    // Build the full URL - logoUrl now comes from database endpoint
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    // Ensure we don't double up on /api
    const cleanBaseUrl = baseUrl.replace(/\/api$/, '');
    
    // logoUrl should be something like /api/organisation-settings/logo/{orgId}
    // Add cache busting parameter to ensure fresh logo loads
    const cacheBuster = `?t=${Date.now()}`;
    return `${cleanBaseUrl}${logoUrl}${cacheBuster}`;
  };
  
  const fullLogoUrl = getFullLogoUrl(organizationSettings?.logoUrl);
  console.log('AIReport - Full Logo URL:', fullLogoUrl);
  console.log('AIReport - Raw Logo URL from settings:', organizationSettings?.logoUrl);
  console.log('AIReport - Organization ID:', organizationSettings?.organizationId);
  
  // Strip Word formatting markers before parsing (for clean browser display)
  const cleanedResponseText = stripWordFormatMarkers(responseText || '');
  
  // Parse the response and extract sections
  const { sections } = parseAIResponse(cleanedResponseText);
  
  
  console.log("AIReport content parsed:", {
    sectionCount: sections?.length || 0,
    hasContent: !!responseText,
    contentLength: responseText?.length || 0,
    visualizationCount: sections?.filter(s => s.type === 'visualization').length || 0,
    textCount: sections?.filter(s => s.type === 'text').length || 0,
    willUseSecondPath: !sections || sections.length === 0
  });
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [processedContent, setProcessedContent] = useState<string>('');
  const [verificationSection, setVerificationSection] = useState<string>('');
  const [mainContent, setMainContent] = useState<string>('');
  
  // Extract verification section from response text
  const extractVerificationSection = (text: string) => {
    const startMarker = '<!-- DATA_VERIFICATION_START -->';
    const endMarker = '<!-- DATA_VERIFICATION_END -->';
    
    const startIndex = text.indexOf(startMarker);
    const endIndex = text.indexOf(endMarker);
    
    if (startIndex !== -1 && endIndex !== -1) {
      const verification = text.substring(startIndex + startMarker.length, endIndex).trim();
      const remaining = text.substring(0, startIndex) + text.substring(endIndex + endMarker.length);
      
      return {
        verification,
        mainContent: remaining.trim()
      };
    }
    
    return {
      verification: '',
      mainContent: text
    };
  };

  // Process markdown content with enhanced formatting
  useEffect(() => {
    // Extract verification section first (using cleaned text without Word markers)
    const { verification, mainContent: contentWithoutVerification } = extractVerificationSection(cleanedResponseText);
    setVerificationSection(verification);
    setMainContent(contentWithoutVerification);
    
    if (sections && sections.length > 0) {
      try {
        // Apply KPI analysis parsing for both normal and high accuracy modes
        const isKPIAnalysis = (
          cleanedResponseText.includes('FINAL VERIFIED CALCULATIONS') || 
          cleanedResponseText.includes('FINAL DEALER PERFORMANCE SCORES') ||
          cleanedResponseText.includes('DETAILED RULE EVALUATION') ||
          cleanedResponseText.includes('FINAL SUMMARY FOR DISPLAY') ||
          cleanedResponseText.includes('KEY INSIGHTS & PATTERNS')
        );
        
        console.log("AIReport processing:", {
          isKPIAnalysisHighAccuracy,
          hasFinaLSummary: cleanedResponseText.includes('FINAL SUMMARY FOR DISPLAY'),
          isKPIAnalysis,
          responseLength: cleanedResponseText.length,
          kpiCodesCount: kpiCodes.length,
          responsePreview: cleanedResponseText.substring(0, 200) + "..."
        });
        
        let processed: string;
        
        if (isKPIAnalysis) {
          // For KPI analysis, process the entire response text to extract final summary
          processed = processMarkdown(cleanedResponseText, true, isKPIAnalysis, kpiCodes);
        } else {
          // For normal content, process sections individually
          processed = sections
            .filter(section => section.type === 'text')
            .map(section => processMarkdown(section.content as string, true, isKPIAnalysis, kpiCodes))
            .join('');
        }
        setProcessedContent(processed);
        console.log("Processed markdown content length:", processed?.length || 0);
        console.log("Is KPI Analysis:", isKPIAnalysis);
      } catch (error) {
        console.error("Error processing markdown content:", error);
      }
    } else {
      // If no sections were parsed but we have content, try to process it directly
      if (cleanedResponseText) {
        try {
          // Check if this is KPI analysis for the fallback path too
          const isKPIAnalysisFallback = (
            cleanedResponseText.includes('FINAL VERIFIED CALCULATIONS') || 
            cleanedResponseText.includes('FINAL DEALER PERFORMANCE SCORES') ||
            cleanedResponseText.includes('DETAILED RULE EVALUATION') ||
            cleanedResponseText.includes('FINAL SUMMARY FOR DISPLAY') ||
            cleanedResponseText.includes('KEY INSIGHTS & PATTERNS')
          );
          
          console.log("Using fallback path with KPI analysis:", isKPIAnalysisFallback);
          const processed = processMarkdown(cleanedResponseText, true, isKPIAnalysisFallback, kpiCodes);
          setProcessedContent(processed);
          console.log("Processed full response as markdown, length:", processed?.length || 0);
        } catch (error) {
          console.error("Error processing full response as markdown:", error);
        }
      }
    }
  }, [sections, cleanedResponseText]);
  
  const toggleExpand = () => setIsExpanded(!isExpanded);



  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write('<html><head><title>Financial Analysis Report</title>');
      printWindow.document.write('<style>');
      printWindow.document.write(`
        body { font-family: Arial, sans-serif; margin: 20px; }
        h1, h2, h3, h4, h5, h6 { margin-top: 20px; }
        
        .table-responsive { overflow-x: auto; margin-bottom: 20px; }
        table.markdown-table { 
          border-collapse: collapse; 
          width: 100%; 
          margin: 20px 0; 
          box-shadow: 0 2px 5px rgba(0,0,0,0.1);
        }
        table.markdown-table th { 
          background-color: #f2f6fc; 
          color: #2a4065; 
          font-weight: 600; 
          padding: 12px 16px; 
          text-align: left; 
          border: 1px solid #e0e0e0; 
        }
        table.markdown-table td { 
          padding: 10px 16px; 
          border: 1px solid #e0e0e0; 
          text-align: left; 
        }
        table.markdown-table tr:nth-child(even) { 
          background-color: #fafafa; 
        }
        
        .visualization-container { 
          margin-bottom: 40px; 
          padding-bottom: 20px;
          border-bottom: 2px solid #e0e0e0;
          page-break-inside: avoid;
        }
        .visualization-title {
          font-size: 1.5rem;
          margin-bottom: 20px;
          color: #2a4065;
          border-bottom: 1px solid #eaeaea;
          padding-bottom: 10px;
        }
        .visualization-item { 
          page-break-inside: avoid; 
          margin-bottom: 20px;
          padding: 15px;
          border: 1px solid #eaeaea;
          border-radius: 8px;
          background-color: #fff;
        }
        .chart-placeholder {
          text-align: center;
          padding: 30px;
          background-color: #f5f8fa;
          border-radius: 6px;
          color: #555;
        }
        .inline-visualization {
          margin: 25px 0;
          padding: 15px;
          border: 1px solid #eaeaea;
          border-radius: 8px;
          background-color: #f5f8fa;
          page-break-inside: avoid;
        }
      `);
      printWindow.document.write('</style></head><body>');
      printWindow.document.write(`<h1>${fileName}</h1>`);
      
      // Use the processed HTML
      printWindow.document.write(`<div>${processedContent}</div>`);
      
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 500);
    }
  };

  return (
    <div className="ai-report-container" style={{ textAlign: 'left' }}>
      <div className="report-header" style={{ textAlign: 'left' }}>
        <div className="report-actions" style={{ textAlign: 'left' }}>
          <button 
            onClick={handlePrint} 
            className="print-button"
            style={{ textAlign: 'left' }}
          >
            Print Report
          </button>
          {verificationSection && (
            <button 
              onClick={() => setShowVerification(!showVerification)}
              className="verification-toggle-button"
              style={{ textAlign: 'left', marginLeft: '10px' }}
            >
              {showVerification ? '🔍 Hide Data Verification' : '🔍 Show Data Verification'}
            </button>
          )}
        </div>
      </div>

      {/* Organization Logo and User Question */}
      <div className="report-branding-section">
        {/* Organization Logo - show org logo if available, otherwise show default Lumina AI logo */}
        <div className="report-org-logo">
          <img 
            src={fullLogoUrl} 
            alt={organizationSettings?.logoUrl ? "Organization Logo" : "Lumina AI"} 
            className="org-logo-image"
            onLoad={(e) => {
              const target = e.target as HTMLImageElement;
              console.log('AIReport - Logo loaded successfully:', target.src);
              console.log('AIReport - Logo natural dimensions:', target.naturalWidth, 'x', target.naturalHeight);
            }}
            onError={(e) => {
              // Fallback to Lumina AI logo if organization logo fails to load
              const target = e.target as HTMLImageElement;
              console.log('AIReport - Logo failed to load:', target.src);
              if (target.src !== "/LuminaAilogoPNG.png") {
                target.src = "/LuminaAilogoPNG.png";
                target.alt = "Lumina AI";
                console.log('AIReport - Falling back to Lumina AI logo');
              }
            }}
          />
        </div>
        
        {/* User Question Display */}
        {userQuestion && userQuestion.trim() && (
          <div className="report-question-section">
            <h3 className="question-heading">Analysis Question</h3>
            <div className="question-content">"{userQuestion}"</div>
          </div>
        )}
      </div>

      {/* Data Verification Section */}
      {verificationSection && showVerification && (
        <div className="verification-section">
          <div 
            dangerouslySetInnerHTML={{
              __html: processMarkdown(verificationSection)
            }}
          />
        </div>
      )}

      <div className="report-content">
        {/* Use processedContent only for KPI analysis, otherwise render sections with visualizations */}
        {processedContent && (cleanedResponseText.includes('FINAL VERIFIED CALCULATIONS') || 
          cleanedResponseText.includes('FINAL DEALER PERFORMANCE SCORES') ||
          cleanedResponseText.includes('DETAILED RULE EVALUATION') ||
          cleanedResponseText.includes('FINAL SUMMARY FOR DISPLAY') ||
          cleanedResponseText.includes('KEY INSIGHTS & PATTERNS')) ? (
          <div className="report-section report-text">
            <div className="report-text-content" dangerouslySetInnerHTML={{ __html: processedContent }} />
          </div>
        ) : sections && sections.length > 0 ? (
          <div className="report-section report-text">
            {sections.map((section, idx) =>
              section.type === 'text' ? (
                <div 
                  key={idx} 
                  className="report-text-content" 
                  dangerouslySetInnerHTML={{ __html: processMarkdown(section.content as string) }} 
                />
              ) : (
                <div key={idx} className="inline-visualization">
                  <ChartErrorBoundary chartId={`report-chart-${idx}`}>
                    <VisualizationRenderer data={section.content as VisualizationData} halfSize={false} />
                  </ChartErrorBoundary>
                </div>
              )
            )}
          </div>
        ) : (
          <div className="report-section report-text">
            <div className="report-text-content">
              <p>No content available to display.</p>
            </div>
          </div>
        )}
        
        {cleanedResponseText && (
          <div className="mt-4">
            <button 
              onClick={toggleExpand}
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {isExpanded ? 'Show Less' : 'Show Full Response'}
            </button>
            {isExpanded && (
              <div className="mt-2 p-4 border border-gray-200 rounded bg-gray-50 overflow-auto max-h-96">
                <pre className="text-xs text-gray-700 whitespace-pre-wrap">{cleanedResponseText}</pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIReport; 